<script lang="ts">
    import { onMount } from "svelte";
    import { Roarr as log } from "roarr";

    import { isReady, name, players, server } from "../store/userdata";
    import { websocket } from "../store/websocket";
    import { getColourClass } from "../styles/nameColourClasses";
    import { convertNameToColour } from "../misc/nameToColour";
    import { basepath, navigateTo } from "../misc/routes";

    let message: string = "";

    $: nameColourClass = getColourClass(convertNameToColour($name));

    onMount(() => {
        if (!$server) {
            server.set(`${location.host}${basepath}/ws`);
        }
    });

    async function onJoinClick() {
        log.info(`Connecting to server ${$server}...`);

        message = "Connecting...";
        const result = await websocket.connect($server);

        if (!result) {
            log.error(`Failed to connect to server ${$server}.`);
            message = `Failed to connect to server '${$server}'.`;
            return;
        }

        try {
            const names = await websocket.start($name);
            $players = [...names, $name];

            log.info(`Connected to ${$server}!`);
            log.info(
                `Other players who are already in the server: ${names.join(
                    ","
                )}`
            );

            $isReady = new Set();
            navigateTo("/lobby");
        } catch (e) {
            log.error(`Could not join server ${$server}, reason: ${e}`);
            message = `Could not join server, reason: ${e}`;
        }
    }

    function onPlaygroundClick() {
        navigateTo("/playground");
    }
</script>

<main class="entry-screen">
    <section class="entry-card">
        <div class="entry-copy">
            <h1>mmikub</h1>
            <h2>Hello <span class={nameColourClass}>{$name || "player"}</span>!</h2>
            <p class="lead">
                Join a live table, sort your rack, and drag tiles across a darker,
                more tactile game surface.
            </p>
        </div>

        <div class="entry-form">
            <label>
                <span>Display name</span>
                <input
                    type="text"
                    placeholder="Name"
                    class={nameColourClass}
                    bind:value={$name}
                />
            </label>

            <label>
                <span>Server</span>
                <input
                    type="text"
                    placeholder="dev.cc-group.org/kummirube/ws"
                    bind:value={$server}
                    on:keypress={(e) => {
                        const keycode = e.code || e.key;
                        if (keycode.includes('Enter')) {
                            onJoinClick();
                        }
                    }}
                />
            </label>

            <div class="entry-actions">
                <button class="primary" on:click={onJoinClick}>Join</button>
                <button class="ghost" on:click={onPlaygroundClick}>Playground</button>
            </div>

            {#if message}
                <p class="message failure">{message}</p>
            {/if}
        </div>
    </section>
</main>

<style>
    .entry-screen {
        min-height: 100vh;
        padding: 32px;
        display: grid;
        place-items: center;
    }

    .entry-card {
        width: min(920px, 100%);
        padding: 42px;
        border-radius: 32px;
        border: 1px solid rgba(126, 173, 255, 0.16);
        background:
            radial-gradient(circle at top right, rgba(255, 186, 84, 0.12), transparent 28%),
            radial-gradient(circle at left, rgba(67, 121, 255, 0.18), transparent 32%),
            linear-gradient(180deg, rgba(13, 28, 48, 0.94), rgba(7, 16, 31, 0.96));
        box-shadow: 0 34px 90px rgba(0, 0, 0, 0.46);
        display: grid;
        grid-template-columns: 1.15fr 0.85fr;
        gap: 28px;
        align-items: end;
    }

    .entry-copy,
    .entry-form {
        display: grid;
        gap: 16px;
    }

    h1 {
        margin: 0;
        color: #f2efe7;
        text-transform: uppercase;
        font-size: clamp(3.8rem, 7vw, 6rem);
        font-weight: 100;
        line-height: 0.95;
        letter-spacing: 0.04em;
        font-family: var(--title-font);
    }

    h1:before {
        content: "gnu";
        color: #ff9f5b;
    }

    h2 {
        margin: 0;
        font-size: clamp(1.6rem, 3vw, 2.2rem);
    }

    .lead {
        margin: 0;
        color: var(--text-muted);
        max-width: 32rem;
        line-height: 1.65;
    }

    label {
        display: grid;
        gap: 8px;
    }

    label span {
        color: var(--text-muted);
        font-size: 0.82rem;
        text-transform: uppercase;
        letter-spacing: 0.14em;
    }

    .entry-actions {
        display: flex;
        gap: 12px;
        padding-top: 8px;
    }

    .primary {
        flex: 1;
    }

    .ghost {
        background: rgba(17, 36, 64, 0.88);
        border-color: rgba(123, 162, 222, 0.22);
    }

    .message {
        margin: 0;
        padding: 1rem 1.1rem;
        border-radius: 16px;
    }

    .failure {
        background: rgba(118, 20, 26, 0.3);
        border: 1px solid rgba(255, 118, 111, 0.35);
        color: #ffd0cf;
    }

    @media (max-width: 820px) {
        .entry-screen {
            padding: 16px;
        }

        .entry-card {
            padding: 24px;
            grid-template-columns: 1fr;
        }

        .entry-actions {
            flex-direction: column;
        }
    }
</style>
